import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const INTERVAL = 5000;
const BANNER_RATIO = "1536 / 308";

const slideHasLink = (s) => s && s.link_type && s.link_type !== "none" && (s.link_value || "").trim();

export default function ImageCarousel({ slides = [], onSlideAction }) {
  const realCount = slides.length;
  const loop = realCount > 1;
  // Clone the first slide at the end for a seamless leftward loop
  const display = loop ? [...slides, slides[0]] : slides;

  const [index, setIndex] = useState(0);
  const [anim, setAnim] = useState(true);

  // Auto-advance (always leftward)
  useEffect(() => {
    if (!loop) return;
    const t = setInterval(() => setIndex((i) => i + 1), INTERVAL);
    return () => clearInterval(t);
  }, [loop, index]);

  // Re-enable the transition on the next frame after an instant reset
  useEffect(() => {
    if (anim) return;
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    return () => cancelAnimationFrame(r);
  }, [anim]);

  if (realCount === 0) return null;

  const handleTransitionEnd = () => {
    if (index === realCount) {
      // We reached the clone (looks identical to the first) -> jump back instantly
      setAnim(false);
      setIndex(0);
    }
  };

  const next = () => setIndex((i) => i + 1);
  const prev = () => {
    if (index === 0) {
      // Jump to the clone position without animation, then slide right into the last
      setAnim(false);
      setIndex(realCount);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setAnim(true);
          setIndex(realCount - 1);
        })
      );
    } else {
      setIndex((i) => i - 1);
    }
  };

  const activeDot = index === realCount ? 0 : index;

  return (
    <section
      className="relative w-full overflow-hidden bg-[var(--lc-ink)]"
      data-testid="home-carousel"
      aria-roledescription="carrusel"
      style={{ aspectRatio: BANNER_RATIO }}
    >
      <div
        className={`flex h-full ${anim ? "transition-transform duration-700 ease-in-out" : ""}`}
        style={{ transform: `translateX(-${index * 100}%)` }}
        onTransitionEnd={handleTransitionEnd}
      >
        {display.map((s, i) => {
          const hasLink = slideHasLink(s);
          return (
            <div key={`${s.id || "s"}-${i}`} className="relative w-full h-full shrink-0" data-testid={`carousel-slide-${i}`}>
              <img
                src={s.image_url}
                alt={s.title || `Imagen promocional ${i + 1}`}
                draggable="false"
                className="absolute inset-0 w-full h-full object-cover select-none"
              />

              {hasLink && (
                <button
                  type="button"
                  onClick={() => onSlideAction?.(s)}
                  aria-label={s.title || "Abrir"}
                  data-testid={`carousel-slide-link-${i}`}
                  className="absolute inset-0 z-[5] cursor-pointer"
                />
              )}

              {(s.title || s.subtitle || s.button_text) && (
                <div className="absolute inset-0 z-10 flex items-end pointer-events-none">
                  <div className="w-full bg-gradient-to-t from-black/70 via-black/25 to-transparent px-5 sm:px-10 pb-3 sm:pb-6 pt-10">
                    {s.title && (
                      <h2 className="font-display font-extrabold text-white text-lg sm:text-3xl max-w-2xl leading-tight drop-shadow-lg" data-testid="carousel-title">
                        {s.title}
                      </h2>
                    )}
                    {s.subtitle && (
                      <p className="mt-0.5 sm:mt-1.5 text-white/90 text-xs sm:text-base max-w-xl leading-snug drop-shadow-md" data-testid="carousel-subtitle">
                        {s.subtitle}
                      </p>
                    )}
                    {s.button_text && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSlideAction?.(s); }}
                        data-testid="carousel-cta"
                        className="pointer-events-auto mt-2 sm:mt-3 inline-flex items-center rounded-full bg-[var(--lc-primary)] hover:bg-[var(--lc-primary-hover)] text-white font-display font-extrabold px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm transition-transform hover:-translate-y-0.5 shadow-[0_8px_18px_-4px_rgba(225,70,90,0.5)]"
                      >
                        {s.button_text}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loop && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Imagen anterior"
            data-testid="carousel-prev"
            className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-20 grid place-items-center w-7 h-7 rounded-full bg-black/25 text-white/90 backdrop-blur-sm hover:bg-black/45 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Imagen siguiente"
            data-testid="carousel-next"
            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-20 grid place-items-center w-7 h-7 rounded-full bg-black/25 text-white/90 backdrop-blur-sm hover:bg-black/45 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id || i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Ir a la imagen ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
                className={`h-2 rounded-full transition-all ${
                  i === activeDot ? "w-6 bg-white" : "w-2 bg-white/60 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
